import React, { useState, useEffect, useRef } from "react";
import {
  X,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader,
  Radio,
  Wifi,
  Shield,
  Zap,
  Waves,
} from "lucide-react";

const RFIDGravingModal = ({
  isOpen,
  onClose,
  userData,
  onSuccess,
  isDarkMode = false,
}) => {
  const [status, setStatus] = useState("connecting"); // connecting, waiting, writing, success, error
  const [message, setMessage] = useState("Connexion au lecteur RFID...");
  const [error, setError] = useState(null);
  const operationIdRef = useRef(null);
  const [cardUid, setCardUid] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [countdown, setCountdown] = useState(null);
  const wsRef = useRef(null);
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && userData) {
      initializeRFIDConnection();
    }

    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userData]);

  const initializeRFIDConnection = () => {
    console.log("🚀 [initializeRFIDConnection] Début de l'initialisation");
    console.log("🚀 [initializeRFIDConnection] isOpen:", isOpen);
    console.log("🚀 [initializeRFIDConnection] userData:", JSON.stringify(userData, null, 2));
    console.log("🚀 [initializeRFIDConnection] Props reçues - onClose:", typeof onClose);
    console.log("🚀 [initializeRFIDConnection] Props reçues - onSuccess:", typeof onSuccess);
    try {
      // Générer un ID d'opération unique
      const opId = `rfid_write_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log("🚀 [initializeRFIDConnection] Opération ID généré:", opId);
      operationIdRef.current = opId;

      // Connexion WebSocket au service RFID
      console.log("🚀 [initializeRFIDConnection] Création WebSocket vers ws://localhost:8080");
      wsRef.current = new WebSocket("ws://localhost:8080");

      wsRef.current.onopen = () => {
        console.log("🔗 Connexion WebSocket RFID établie");
        console.log("📤 Opération ID:", opId);
        console.log("👤 Données utilisateur:", userData);
        console.log("🔗 [onopen] WebSocket readyState:", wsRef.current.readyState);
        setConnectionStatus("connected");
        setStatus("waiting");
        setMessage("Approchez une carte RFID du lecteur...");

        // Fonction pour envoyer l'opération quand la connexion est prête
        const startOperation = () => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            console.log("📤 Envoi de l'opération de gravure...");
            const message = {
              type: "start_write_operation",
              operationId: opId,
              userData: userData,
            };
            console.log("📤 Message envoyé:", JSON.stringify(message, null, 2));
            try {
              wsRef.current.send(JSON.stringify(message));
              console.log("✅ Message envoyé avec succès");
            } catch (error) {
              console.error("❌ Erreur envoi message:", error);
              setError("Erreur de communication avec le service RFID");
              setStatus("error");
            }
          } else {
            console.warn("⚠️ WebSocket pas encore prêt, nouvel essai...");
            setTimeout(startOperation, 100);
          }
        };

        // Démarrer l'opération après un court délai
        setTimeout(startOperation, 200);
      };

      wsRef.current.onmessage = (event) => {
        try {
          console.log("📨 Message WebSocket brut reçu:", event.data);
          const data = JSON.parse(event.data);
          console.log("📨 Message WebSocket parsé:", JSON.stringify(data, null, 2));
          console.log("🆔 Opération ID actuelle:", operationIdRef.current);
          console.log("🆔 Opération ID du message:", data.operationId);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error("❌ Erreur parsing message WebSocket:", error);
          console.error("📨 Message brut qui a causé l'erreur:", event.data);
          setError("Erreur de communication avec le service RFID");
          setStatus("error");
        }
      };

      wsRef.current.onclose = () => {
        console.log("❌ Connexion WebSocket RFID fermée");
        setConnectionStatus("disconnected");
        if (status !== "success" && status !== "error") {
          setError("Connexion au service RFID perdue");
          setStatus("error");
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("❌ Erreur WebSocket RFID:", error);
        setConnectionStatus("disconnected");
        setError(
          "Impossible de se connecter au service RFID. Vérifiez que le serveur est démarré.",
        );
        setStatus("error");
      };

      // Timeout après 30 secondes
      timeoutRef.current = setTimeout(() => {
        if (status === "waiting") {
          setError("Timeout - Aucune carte détectée dans les 30 secondes");
          setStatus("error");
        }
      }, 30000);
    } catch (error) {
      console.error("❌ [initializeRFIDConnection] Erreur initialisation RFID:", error);
      setError("Erreur lors de l'initialisation du service RFID");
      setStatus("error");
    }
    console.log("🚀 [initializeRFIDConnection] Fin de l'initialisation");
  };

  const handleWebSocketMessage = (data) => {
    console.log("📨 [handleWebSocketMessage] Type:", data.type);
    console.log("📨 [handleWebSocketMessage] Données complètes:", JSON.stringify(data, null, 2));
    console.log("🔄 [handleWebSocketMessage] Status actuel du modal:", status);
    console.log("🆔 [handleWebSocketMessage] OperationId modal:", operationIdRef.current);
    console.log("🆔 [handleWebSocketMessage] OperationId message:", data.operationId);

    // Utiliser la ref pour avoir la valeur actuelle
    const currentOpId = operationIdRef.current;

    switch (data.type) {
      case "readers_status":
        if (data.readers && data.readers.length > 0) {
          console.log("📡 Lecteurs disponibles:", data.readers);
        } else {
          setError(
            "Aucun lecteur RFID détecté. Vérifiez que le lecteur ACR122U est connecté.",
          );
          setStatus("error");
        }
        break;

      case "reader_connected":
        console.log("📡 Lecteur connecté:", data.reader);
        break;

      case "reader_disconnected":
        setError("Lecteur RFID déconnecté");
        setStatus("error");
        break;

      case "operation_started":
        console.log("🔄 [operation_started] Comparaison IDs:", {
          messageId: data.operationId,
          modalId: currentOpId,
          match: data.operationId === currentOpId
        });
        if (data.operationId === currentOpId) {
          console.log("✅ [operation_started] IDs correspondent, mise à jour du status");
          setStatus("waiting");
          setMessage(data.message || "Approchez une carte RFID du lecteur...");
        } else {
          console.log("⚠️ [operation_started] IDs ne correspondent pas, ignoré");
        }
        break;

      case "write_success":
        console.log("🎉 [write_success] Comparaison IDs:", {
          messageId: data.operationId,
          modalId: currentOpId,
          match: data.operationId === currentOpId
        });
        console.log("🎉 [write_success] Données carte:", {
          cardUid: data.cardUid,
          userId: userData?.id,
          username: userData?.username
        });
        if (data.operationId === currentOpId) {
          console.log("✅ [write_success] IDs correspondent, gravure réussie!");
          console.log("🔍 [write_success] Données utilisateur complètes:", JSON.stringify(userData, null, 2));
          console.log("🔍 [write_success] Statut avant callback:", status);

          setStatus("success");
          setCardUid(data.cardUid);
          setMessage("Carte MOO SYNC enregistrée avec succès!");

          // Appeler le callback de succès
          if (onSuccess) {
            console.log("📞 [write_success] Appel du callback onSuccess");
            console.log("📞 [write_success] Données envoyées au callback:", {
              cardUid: data.cardUid,
              userId: userData?.id,
              username: userData?.username,
            });

            try {
              onSuccess({
                cardUid: data.cardUid,
                userId: userData.id,
                username: userData.username,
              });
              console.log("✅ [write_success] Callback onSuccess exécuté avec succès");
            } catch (callbackError) {
              console.error("❌ [write_success] Erreur dans callback onSuccess:", callbackError);
            }
          } else {
            console.log("⚠️ [write_success] Aucun callback onSuccess fourni");
          }

          // Démarrer le compte à rebours de 7 secondes
          console.log("⏰ [write_success] Démarrage du compte à rebours de 7 secondes");
          setCountdown(7);
          countdownRef.current = setInterval(() => {
            setCountdown(prev => {
              console.log(`⏰ Compte à rebours: ${prev - 1}s restantes`);
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                console.log("🚪 [write_success] Fermeture automatique du modal après compte à rebours");
                onClose();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          console.log("⚠️ [write_success] IDs ne correspondent pas, ignoré");
        }
        break;

      case "write_error":
        console.log("❌ [write_error] Comparaison IDs:", {
          messageId: data.operationId,
          modalId: currentOpId,
          match: data.operationId === currentOpId
        });
        if (data.operationId === currentOpId) {
          console.log("❌ [write_error] IDs correspondent, erreur de gravure");
          setStatus("error");
          setError(data.error || "Erreur lors de l'enregistrement de la carte");
        } else {
          console.log("⚠️ [write_error] IDs ne correspondent pas, ignoré");
        }
        break;

      case "operation_timeout":
        console.log("⏰ [operation_timeout] Comparaison IDs:", {
          messageId: data.operationId,
          modalId: currentOpId,
          match: data.operationId === currentOpId
        });
        if (data.operationId === currentOpId) {
          console.log("⏰ [operation_timeout] IDs correspondent, timeout");
          setStatus("error");
          setError("Timeout - Opération annulée");
        } else {
          console.log("⚠️ [operation_timeout] IDs ne correspondent pas, ignoré");
        }
        break;

      case "card_removed":
        if (status === "writing") {
          setError(
            "Carte retirée pendant l'enregistrement. Veuillez réessayer.",
          );
          setStatus("error");
        }
        break;

      case "reader_error":
        setError(`Erreur lecteur: ${data.error}`);
        setStatus("error");
        break;

      default:
        console.warn("⚠️ [default] Message WebSocket non géré:", data.type);
        console.warn("⚠️ [default] Données complètes:", JSON.stringify(data, null, 2));
    }
  };

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        // Annuler l'opération en cours
        if (operationIdRef.current) {
          try {
            wsRef.current.send(
              JSON.stringify({
                type: "cancel_operation",
                operationId: operationIdRef.current,
              }),
            );
          } catch (error) {
            console.warn("⚠️ Erreur annulation opération:", error);
          }
        }
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    // Reset des états
    setStatus("connecting");
    setMessage("Connexion au lecteur RFID...");
    setError(null);
    operationIdRef.current = null;
    setCardUid(null);
    setConnectionStatus("disconnected");
    setCountdown(null);
  };

  const handleClose = () => {
    console.log("🚪 [handleClose] Fermeture manuelle du modal");
    console.log("🚪 [handleClose] Statut actuel:", status);
    console.log("🚪 [handleClose] Compte à rebours actuel:", countdown);
    cleanup();
    onClose();
  };

  const handleRetry = () => {
    cleanup();
    setTimeout(() => {
      initializeRFIDConnection();
    }, 500);
  };

  const getStatusIcon = () => {
    switch (status) {
      case "connecting":
        return (
          <div className="relative">
            <Loader className="animate-spin text-blue-500" size={48} />
            <div className="absolute inset-0 animate-ping">
              <Wifi className="text-blue-300 opacity-50" size={48} />
            </div>
          </div>
        );
      case "waiting":
        return (
          <div className="relative">
            <Radio className="animate-pulse text-yellow-500" size={48} />
            <div className="absolute -inset-2">
              <Waves className="text-yellow-300 opacity-30 animate-bounce" size={52} />
            </div>
          </div>
        );
      case "writing":
        return (
          <div className="relative">
            <Loader className="animate-spin text-blue-500" size={48} />
            <Zap className="absolute -top-1 -right-1 text-yellow-400 animate-ping" size={16} />
          </div>
        );
      case "success":
        return (
          <div className="relative">
            <CheckCircle className="text-green-500" size={48} />
            <div className="absolute inset-0 animate-ping">
              <CheckCircle className="text-green-300 opacity-50" size={48} />
            </div>
          </div>
        );
      case "error":
        return (
          <div className="relative">
            <AlertCircle className="text-red-500" size={48} />
            <div className="absolute inset-0 animate-pulse">
              <AlertCircle className="text-red-300 opacity-30" size={48} />
            </div>
          </div>
        );
      default:
        return <CreditCard className="text-gray-400" size={48} />;
    }
  };

  if (!isOpen || !userData) return null;

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const panelClasses = isDarkMode
    ? "bg-[#0f172a] text-slate-100 border border-white/10"
    : "bg-white text-slate-900 border border-slate-100";

  const statusPanels = {
    connecting: isDarkMode
      ? "bg-sky-500/10 border-sky-500/30"
      : "bg-sky-50 border-sky-200",
    waiting: isDarkMode
      ? "bg-amber-500/10 border-amber-500/30"
      : "bg-amber-50 border-amber-200",
    writing: isDarkMode
      ? "bg-indigo-500/10 border-indigo-500/30"
      : "bg-indigo-50 border-indigo-200",
    success: isDarkMode
      ? "bg-emerald-500/10 border-emerald-500/30"
      : "bg-emerald-50 border-emerald-200",
    error: isDarkMode
      ? "bg-rose-500/10 border-rose-500/30"
      : "bg-rose-50 border-rose-200",
  };

  const statusCardClass =
    statusPanels[status] ||
    (isDarkMode
      ? "bg-white/5 border-white/10"
      : "bg-slate-50 border-slate-200");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur">
      <div
        className={`${panelClasses} w-full max-w-lg rounded-3xl p-6 shadow-2xl space-y-6`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
              Gravure RFID
            </p>
            <h2 className="text-2xl font-semibold">
              {userData.first_name} {userData.last_name}
            </h2>
            <p className={`text-sm ${textMuted}`}>@{userData.username}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={status === "writing"}
            className="rounded-full border border-white/10 p-2 hover:border-amber-400/60 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className={`rounded-2xl p-5 border ${statusCardClass}`}>
          <div className="flex flex-col items-center gap-4 text-center">
            {getStatusIcon()}
            <div>
              <p className="text-lg font-semibold mb-1">{message}</p>
              {error && (
                <p className="text-sm text-rose-400 font-medium">{error}</p>
              )}
              {cardUid && status === "success" && (
                <div className="mt-3 text-sm">
                  <p className="font-semibold text-emerald-400">
                    Carte enregistrée
                  </p>
                  <p className="font-mono text-xs mt-1 bg-black/20 rounded-lg px-2 py-1 inline-flex gap-2">
                    <CreditCard size={14} />
                    UID: {cardUid}
                  </p>
                  {countdown && (
                    <p className={`mt-2 text-xs ${textMuted}`}>
                      Fermeture dans {countdown}s
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`rounded-2xl border border-white/10 p-4 flex items-start gap-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}
        >
          <div className="p-2 rounded-full bg-amber-400/20 text-amber-300">
            <Shield size={20} />
          </div>
          <div className="text-sm">
            <p className="font-semibold">Instructions</p>
            <p className={textMuted}>
              Approchez une carte MOO du lecteur RFID. L'opération s'arrête
              automatiquement après la gravure ou en cas d'erreur.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <div
              className={`h-2 w-2 rounded-full ${connectionStatus === "connected" ? "bg-emerald-400" : "bg-rose-400"}`}
            ></div>
            <span className={textMuted}>
              {connectionStatus === "connected"
                ? "Lecteur connecté"
                : "En attente du lecteur"}
            </span>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleRetry}
              disabled={status === "writing"}
              className="rounded-2xl border border-white/10 px-3 py-2 text-xs font-semibold hover:border-amber-400/60 disabled:opacity-50"
            >
              Réessayer
            </button>
            <button
              onClick={handleClose}
              disabled={status === "writing"}
              className="rounded-2xl border border-white/10 px-3 py-2 text-xs font-semibold hover:border-rose-400/60 disabled:opacity-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFIDGravingModal;
